export interface ApiBatchResult<T> {
  results: T[];
  totalPages: number;
  totalResults: number;
}

export interface FetchFilledDisplayPageOptions<T> {
  displayPage: number;
  displaySize: number;
  apiBatchSize: number;
  fetchApiBatch: (
    apiPage: number,
    batchLimit: number
  ) => Promise<ApiBatchResult<T>>;
  /** Merged into the first API batch only (e.g. paid pay links on business page 1). */
  prependToFirstBatch?: T[];
  sortItems?: (items: T[]) => T[];
}

/**
 * Walks sequential API pages (and optional prepended rows) until it can return
 * exactly `displaySize` items for `displayPage`, accounting for client-side drops.
 */
export async function fetchFilledDisplayPage<T>({
  displayPage,
  displaySize,
  apiBatchSize,
  fetchApiBatch,
  prependToFirstBatch = [],
  sortItems,
}: FetchFilledDisplayPageOptions<T>): Promise<{
  results: T[];
  totalPages: number;
  totalResults: number;
  page: number;
  limit: number;
}> {
  const safeDisplayPage = Math.max(1, displayPage);
  const safeDisplaySize = Math.max(1, displaySize);
  const skip = (safeDisplayPage - 1) * safeDisplaySize;
  const collected: T[] = [];
  let skipped = 0;
  let apiPage = 1;
  let apiTotalResults = 0;
  let apiTotalPages = 1;
  let prependMerged = false;

  while (collected.length < safeDisplaySize) {
    const batch = await fetchApiBatch(apiPage, apiBatchSize);
    apiTotalResults = batch.totalResults;
    apiTotalPages = batch.totalPages;

    let items = [...batch.results];
    if (!prependMerged && prependToFirstBatch.length > 0) {
      items = [...prependToFirstBatch, ...items];
      prependMerged = true;
    }
    if (sortItems) {
      items = sortItems(items);
    }

    for (const item of items) {
      if (skipped < skip) {
        skipped += 1;
      } else if (collected.length < safeDisplaySize) {
        collected.push(item);
      }
    }

    if (apiPage >= apiTotalPages) {
      break;
    }
    apiPage += 1;
  }

  const adjustedTotal =
    apiTotalResults +
    (prependToFirstBatch.length > 0 ? prependToFirstBatch.length : 0);
  const totalPages = Math.max(1, Math.ceil(adjustedTotal / safeDisplaySize));

  return {
    results: collected,
    totalPages,
    totalResults: adjustedTotal,
    page: safeDisplayPage,
    limit: safeDisplaySize,
  };
}
